import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from './common/Layout';
import SharingInfoDisplay from './common/SharingInfoDisplay';
import { axiosInstance } from '../services/api';
import { CATEGORIES, ILLNESS_SYMPTOMS } from '../constants/logCategories';
import { getUser } from '../utils/secureStorage';

const FONT_HEADING = "'Lora', Georgia, serif";
const FONT_BODY = "'Source Sans 3', sans-serif";

// Severity labels
const SEVERITY_LABELS = {
  mild: 'Mild \u2014 stayed home',
  moderate: 'Moderate \u2014 saw a doctor',
  severe: 'Severe \u2014 hospitalized',
  critical: 'Very severe \u2014 ICU',
};

// Condition status labels
const STATUS_LABELS = {
  ongoing: 'Ongoing / Managing',
  resolved: 'Resolved',
  passed_away: 'Passed away from this',
  not_sure: 'Not sure',
};

// Family pattern labels
const FAMILY_PATTERN_LABELS = {
  yes: 'Yes',
  not_sure: 'Unsure',
  no: 'No',
};

function getSubTypeLabel(category, subType, subTypeCustom) {
  if (!subType) return null;
  if (subType === 'other') return subTypeCustom || 'Other';
  const cat = CATEGORIES.find(c => c.key === category);
  const pill = cat?.pills.find(p => p.value === subType);
  return pill?.label || subType.replace(/_/g, ' ');
}

function getSubTypeIcon(category, subType) {
  const cat = CATEGORIES.find(c => c.key === category);
  const pill = cat?.pills.find(p => p.value === subType);
  return pill?.icon || null;
}

const DetailRow = ({ label, value, icon }) => {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 0',
      borderBottom: '1px solid #F0EDE4',
    }}>
      {icon && <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{icon}</span>}
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: '#999',
          textTransform: 'uppercase', letterSpacing: '0.3px',
          marginBottom: 4, fontFamily: FONT_BODY,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 16, color: '#333', fontFamily: FONT_BODY, lineHeight: 1.5,
        }}>
          {value}
        </div>
      </div>
    </div>
  );
};

const LogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Get current user for ownership check
  const currentUser = (() => {
    try {
      return getUser();
    } catch { return null; }
  })();

  useEffect(() => {
    const fetchLog = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/logs/${id}/`);
        setLog(res.data);
      } catch (err) {
        console.error('Failed to load log:', err);
        alert('Log entry not found.');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchLog();
  }, [id, navigate]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/logs/${id}/`);
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to delete log:', err);
      alert('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{
          background: '#FAF9F5', minHeight: '100vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>{'\u{1F4CB}'}</div>
            <p style={{ fontSize: 17, color: '#999', fontFamily: FONT_BODY }}>Loading log...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!log) return null;

  const catConfig = CATEGORIES.find(c => c.key === log.category);
  const catColor = catConfig?.color || '#4a6178';
  const catLight = catConfig?.colorLight || '#eaf0f6';
  const subTypeLabel = getSubTypeLabel(log.category, log.sub_type, log.sub_type_custom);
  const subTypeIcon = getSubTypeIcon(log.category, log.sub_type);
  const sd = log.structured_data || {};

  const title = subTypeLabel
    ? `${catConfig?.label || 'Log'} \u2014 ${subTypeLabel}`
    : catConfig?.label || 'Log Entry';

  // Build symptom display
  const symptomLabels = (sd.symptoms || []).map(s => {
    const sym = ILLNESS_SYMPTOMS.find(is => is.value === s);
    return sym ? `${sym.icon} ${sym.label}` : s;
  });
  if (sd.custom_symptom) symptomLabels.push(sd.custom_symptom);

  // Format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      });
    } catch { return dateStr; }
  };

  return (
    <Layout>
      <div style={{ background: '#FAF9F5', minHeight: '100vh' }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${catLight} 0%, #fff 100%)`,
          borderBottom: `3px solid ${catColor}`,
          padding: '20px 28px',
        }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.7)', border: '1.5px solid #D4CFC4',
                borderRadius: 10, padding: '9px 18px', cursor: 'pointer',
                fontSize: 15, fontWeight: 600, color: '#556B2F',
                fontFamily: FONT_BODY, marginBottom: 14,
                transition: 'background 0.15s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
              </svg>
              Back to Hub
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: catColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, color: '#fff',
              }}>
                {catConfig?.icon || '\u{1F4CB}'}
              </div>
              <div style={{ flex: 1 }}>
                <h1 style={{
                  fontSize: 26, fontWeight: 800, color: '#2C3E1A', margin: 0,
                  fontFamily: FONT_HEADING,
                }}>
                  {title}
                </h1>
                <p style={{
                  fontSize: 15, color: '#666', margin: '4px 0 0',
                  fontFamily: FONT_BODY,
                }}>
                  {log.subject_name_display || log.subject_name || 'Unknown'}
                  {log.created_at && (
                    <span style={{ color: '#bbb' }}> &middot; {formatDate(log.created_at)}</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 28px 120px' }}>
          <div style={{
            background: '#fff', borderRadius: 20,
            border: '1px solid #E8E4DA',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            padding: '8px 28px 28px',
          }}>
            {/* Sharing Info */}
            <SharingInfoDisplay
              sharingScope={log.sharing_scope}
              originFamilyId={log.origin_family}
              sharedWithFamilies={log.shared_with_families}
              isOwner={currentUser && log.author === currentUser.person_id}
              className="pt-3 pb-1"
            />

            {/* Who */}
            <DetailRow
              label="Who"
              value={log.subject_name_display || log.subject_name}
              icon={'\u{1F464}'}
            />

            {/* Type */}
            {subTypeLabel && (
              <DetailRow
                label="Type"
                value={subTypeLabel}
                icon={subTypeIcon || catConfig?.icon}
              />
            )}

            {/* When */}
            {(log.date_text || log.date_start) && (
              <DetailRow
                label="When"
                value={
                  log.date_text ||
                  (log.date_start && log.date_end
                    ? `${formatDate(log.date_start)} \u2013 ${formatDate(log.date_end)}`
                    : formatDate(log.date_start))
                }
                icon={'\u{1F4C5}'}
              />
            )}

            {/* Category-specific details */}
            {log.category === 'illness' && (
              <>
                {sd.severity && (
                  <DetailRow
                    label="Severity"
                    value={SEVERITY_LABELS[sd.severity] || sd.severity}
                    icon={'\u{1F321}\uFE0F'}
                  />
                )}
                {symptomLabels.length > 0 && (
                  <DetailRow
                    label="Symptoms"
                    value={symptomLabels.join(', ')}
                    icon={'\u{1F912}'}
                  />
                )}
                {log.date_start && (
                  <DetailRow
                    label="Duration"
                    value={
                      log.date_end
                        ? `${formatDate(log.date_start)} \u2013 ${formatDate(log.date_end)}`
                        : `Started ${formatDate(log.date_start)}`
                    }
                    icon={'\u23F1\uFE0F'}
                  />
                )}
              </>
            )}

            {log.category === 'condition' && (
              <>
                {sd.status && (
                  <DetailRow
                    label="Status"
                    value={STATUS_LABELS[sd.status] || sd.status}
                    icon={'\u{1FAC0}'}
                  />
                )}
                {sd.family_pattern && (
                  <DetailRow
                    label="Runs in the family?"
                    value={FAMILY_PATTERN_LABELS[sd.family_pattern] || sd.family_pattern}
                    icon={'\u{1F46A}'}
                  />
                )}
              </>
            )}

            {log.category === 'vehicle' && (
              <>
                {(sd.year || sd.make || sd.model) && (
                  <DetailRow
                    label="Vehicle"
                    value={[sd.year, sd.make, sd.model].filter(Boolean).join(' ')}
                    icon={'\u{1F697}'}
                  />
                )}
                {sd.color && (
                  <DetailRow
                    label="Color"
                    value={sd.color}
                    icon={'\u{1F3A8}'}
                  />
                )}
              </>
            )}

            {/* Location */}
            {log.location && (
              <DetailRow label="Location" value={log.location} icon={'\u{1F4CD}'} />
            )}

            {/* Notes */}
            {log.note && (
              <DetailRow label="Notes" value={log.note} icon={'\u{1F4DD}'} />
            )}
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end',
          }}>
            <button
              onClick={() => navigate(`/logs/${id}/edit`)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', borderRadius: 14,
                border: `2px solid ${catColor}`, background: '#fff',
                color: catColor, cursor: 'pointer',
                fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = catLight; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', borderRadius: 14,
                border: '2px solid #E8E4DA', background: '#fff',
                color: '#999', cursor: 'pointer',
                fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#FFF5F5'; e.currentTarget.style.color = '#D32F2F'; e.currentTarget.style.borderColor = '#FFCDD2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#999'; e.currentTarget.style.borderColor = '#E8E4DA'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Delete
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(44, 62, 26, 0.45)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
            onClick={() => setShowDeleteConfirm(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 20, padding: 32,
                maxWidth: 420, width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                animation: 'logDetailModalIn 0.25s ease',
              }}
            >
              <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 16 }}>{'\u{1F5D1}\uFE0F'}</div>
              <h3 style={{
                fontSize: 20, fontWeight: 800, color: '#2C3E1A', margin: '0 0 8px',
                fontFamily: FONT_HEADING, textAlign: 'center',
              }}>
                Delete this log?
              </h3>
              <p style={{
                fontSize: 15, color: '#888', margin: '0 0 24px',
                fontFamily: FONT_BODY, textAlign: 'center', lineHeight: 1.5,
              }}>
                This action cannot be undone. The log entry will be permanently removed.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    flex: 1, padding: '12px 20px', borderRadius: 12,
                    border: '2px solid #E8E4DA', background: '#fff',
                    color: '#666', cursor: 'pointer',
                    fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{
                    flex: 1, padding: '12px 20px', borderRadius: 12,
                    border: 'none', background: '#D32F2F',
                    color: '#fff', cursor: deleting ? 'default' : 'pointer',
                    fontSize: 16, fontWeight: 700, fontFamily: FONT_BODY,
                    opacity: deleting ? 0.7 : 1,
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes logDetailModalIn {
            from { opacity: 0; transform: translateY(-16px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>
    </Layout>
  );
};

export default LogDetail;
